/** The product's table: typed columns, client sort, density, selection, row hover actions and a
 * hover preview, header hints and menus, loading / empty / error states, and a footer slot.
 *
 * **It never computes a total.** Counts and sums come in as props: `pagination.total` is the
 * server's, and `footer` is whatever the screen prints ("Showing 25 of 1,201 figures"). A sort here
 * orders the rows it was given and says so in its own docs: when the API pages, the page is sorted,
 * not the dataset, so a server-sorted screen passes `sortMode="server"` and handles `onSortChange`.
 *
 * Interaction contract:
 * - A header is a sort button when its column is `sortable`; `aria-sort` is on the `th`.
 * - `onRowOpen` makes a row open on click, Enter or Space (the row is a tab stop). Links, buttons and
 *   inputs inside the row keep their own behaviour and do not also open the row, and a click that
 *   ends a text selection does not open it either. ArrowUp/Down move between rows.
 * - `rowActions` is a cell revealed on hover or focus-within (always visible on touch).
 * - `rowPreview` shows a `PreviewCard` after a hover delay or on keyboard focus; it is never the only
 *   place a fact lives.
 * - The scroll container becomes a labelled, focusable region only when it actually overflows.
 */

import {
  CaretDown,
  CaretUp,
  CaretUpDown,
  ColumnsPlusRight,
  Info,
  ListDashes,
  Rows,
} from "@phosphor-icons/react";
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent, MouseEvent, ReactNode, RefObject } from "react";

import { quantity } from "../../lib/format";
import styles from "./DataTable.module.css";
import { EmptyState } from "./EmptyState";
import { PreviewCard } from "./HoverCard";
import { Skeleton } from "./Skeleton";
import { sortRows } from "./sortRows";
import type { SortDirection, SortState } from "./sortRows";
import { usePopup } from "./usePopup";

export type { SortDirection, SortState };
export type Density = "comfortable" | "compact";

export interface Column<T> {
  id: string;
  /** Header text. A string, because it is also the accessible name of the sort and menu controls. */
  header: string;
  /** Render the cell. Defaults to the accessor's value as text. */
  cell?: (row: T) => ReactNode;
  /** The value to sort by (and to print when there is no `cell`). `null` sorts last. */
  accessor?: (row: T) => string | number | null | undefined;
  align?: "left" | "right" | "center";
  /** Any CSS width: `160px`, `20%`. */
  width?: string;
  sortable?: boolean;
  /** An explanation shown from an info button beside the header (hover, focus). */
  hint?: ReactNode;
  /** The header menu and the Columns menu may hide it. */
  hideable?: boolean;
}

export interface Pagination {
  /** 1-based. */
  page: number;
  pageSize: number;
  /** The server's total across all pages. */
  total: number;
  onPage: (page: number) => void;
}

export interface DataTableProps<T> {
  /** The table's accessible name, and the scroll region's when it overflows. */
  caption: string;
  columns: readonly Column<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  /** A human name for a row: used in selection checkboxes and the open hint. */
  rowLabel?: (row: T) => string;
  onRowOpen?: (row: T) => void;
  rowActions?: (row: T) => ReactNode;
  rowPreview?: (row: T) => ReactNode;
  previewDelayMs?: number;

  loading?: boolean;
  loadingRows?: number;
  /** Shown in place of the rows. A failed read is never the empty state. */
  error?: ReactNode;
  empty?: ReactNode;

  defaultSort?: SortState | null;
  sort?: SortState | null;
  onSortChange?: (sort: SortState | null) => void;
  /** `server`: rows arrive in order; the table only reports the choice. */
  sortMode?: "client" | "server";

  selectable?: boolean;
  selectedKeys?: ReadonlySet<string>;
  onSelectionChange?: (next: ReadonlySet<string>) => void;

  density?: Density;
  defaultDensity?: Density;
  onDensityChange?: (density: Density) => void;
  showDensityToggle?: boolean;

  stickyFirstColumn?: boolean;
  /** Scroll the body inside the table so the sticky header has something to stick to. */
  maxHeight?: string;

  footer?: ReactNode;
  pagination?: Pagination;
  testId?: string;
}

/** A count with thousands separators, through the product's one formatter. */
const number = { format: (value: number): string => quantity(String(value)) };

function useOverflow(ref: RefObject<HTMLElement | null>): boolean {
  const [overflows, setOverflows] = useState(false);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const check = () => setOverflows(node.scrollWidth > node.clientWidth + 1);
    check();
    window.addEventListener("resize", check);
    const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(check);
    observer?.observe(node);
    return () => {
      window.removeEventListener("resize", check);
      observer?.disconnect();
    };
  }, [ref]);
  return overflows;
}

interface MenuItem {
  id: string;
  label: string;
  checked?: boolean;
  disabled?: boolean;
  onSelect: () => void;
  /** Leave the menu open after choosing (a checkbox list). */
  keepOpen?: boolean;
}

/** A button that opens a menu of commands. Roving focus; Escape closes and returns to the button. */
function MenuButton({
  label,
  items,
  children,
  className,
  align = "start",
  testId,
}: {
  label: string;
  items: readonly MenuItem[];
  children: ReactNode;
  className?: string;
  align?: "start" | "end";
  testId?: string;
}) {
  const { open, setOpen, close, triggerRef, popupRef } = usePopup();
  const [index, setIndex] = useState(0);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const menuId = useId();

  const show = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setAnchor(rect);
    setIndex(0);
    setOpen(true);
  };
  useEffect(() => {
    if (open) refs.current[index]?.focus();
  }, [open, index]);

  const enabled = items.map((item, i) => (item.disabled ? -1 : i)).filter((i) => i >= 0);
  const move = (delta: number) => {
    const at = enabled.indexOf(index);
    const next = enabled[Math.max(0, Math.min(enabled.length - 1, at + delta))];
    if (next !== undefined) setIndex(next);
  };
  const onKey = (event: KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        move(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        move(-1);
        break;
      case "Home":
        event.preventDefault();
        if (enabled[0] !== undefined) setIndex(enabled[0]);
        break;
      case "End":
        event.preventDefault();
        if (enabled.length) setIndex(enabled[enabled.length - 1] as number);
        break;
      case "Escape":
        event.preventDefault();
        event.stopPropagation();
        close();
        break;
      case "Tab":
        setOpen(false);
        break;
      default:
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={className}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        data-testid={testId}
        onClick={(event) => {
          event.stopPropagation();
          if (open) close(false);
          else show();
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" && !open) {
            event.preventDefault();
            show();
          }
        }}
      >
        {children}
      </button>
      {open && anchor ? (
        <PreviewCard
          anchor={anchor}
          id={menuId}
          role="menu"
          ariaLabel={label}
          align={align}
          className={styles.menu}
          popupRef={popupRef}
          onKeyDown={onKey}
          testId="table-menu"
        >
          {items.map((item, i) => (
            <button
              key={item.id}
              ref={(node) => {
                refs.current[i] = node;
              }}
              type="button"
              role={item.checked === undefined ? "menuitem" : "menuitemcheckbox"}
              aria-checked={item.checked}
              disabled={item.disabled}
              tabIndex={i === index ? 0 : -1}
              className={styles.menuItem}
              onClick={() => {
                item.onSelect();
                if (!item.keepOpen) close();
              }}
            >
              <span className={styles.menuTick} aria-hidden="true">
                {item.checked ? "✓" : ""}
              </span>
              {item.label}
            </button>
          ))}
        </PreviewCard>
      ) : null}
    </>
  );
}

/** The info button beside a header: the column's explanation on hover or focus, Escape to dismiss. */
function HeaderHint({ header, hint }: { header: string; hint: ReactNode }) {
  const id = useId();
  const ref = useRef<HTMLButtonElement>(null);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const show = () => {
    const rect = ref.current?.getBoundingClientRect();
    if (rect) setAnchor(rect);
  };
  return (
    <>
      <button
        ref={ref}
        type="button"
        className={styles.iconBtn}
        aria-label={`About ${header}`}
        aria-describedby={anchor ? id : undefined}
        onPointerEnter={show}
        onPointerLeave={() => setAnchor(null)}
        onFocus={show}
        onBlur={() => setAnchor(null)}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === "Escape" && anchor) {
            event.stopPropagation();
            setAnchor(null);
          }
        }}
      >
        <Info size={14} aria-hidden="true" />
      </button>
      {anchor ? (
        <PreviewCard anchor={anchor} id={id} testId="header-hint">
          {hint}
        </PreviewCard>
      ) : null}
    </>
  );
}

export function DataTable<T>({
  caption,
  columns,
  rows,
  rowKey,
  rowLabel,
  onRowOpen,
  rowActions,
  rowPreview,
  previewDelayMs = 450,
  loading = false,
  loadingRows = 6,
  error,
  empty,
  defaultSort = null,
  sort: controlledSort,
  onSortChange,
  sortMode = "client",
  selectable = false,
  selectedKeys,
  onSelectionChange,
  density: controlledDensity,
  defaultDensity = "comfortable",
  onDensityChange,
  showDensityToggle = false,
  stickyFirstColumn = false,
  maxHeight,
  footer,
  pagination,
  testId = "data-table",
}: DataTableProps<T>) {
  const [innerSort, setInnerSort] = useState<SortState | null>(defaultSort);
  const sort = controlledSort === undefined ? innerSort : controlledSort;
  const [innerDensity, setInnerDensity] = useState<Density>(defaultDensity);
  const density = controlledDensity ?? innerDensity;
  const [innerSelected, setInnerSelected] = useState<ReadonlySet<string>>(new Set());
  const selected = selectedKeys ?? innerSelected;
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());

  const visible = useMemo(() => columns.filter((c) => !hidden.has(c.id)), [columns, hidden]);
  const sorted = useMemo(() => {
    if (sortMode === "server" || !sort) return rows;
    return sortRows(rows, columns.find((c) => c.id === sort.columnId)?.accessor, sort.direction);
  }, [rows, columns, sort, sortMode]);

  const scroller = useRef<HTMLDivElement>(null);
  const overflows = useOverflow(scroller);
  const captionId = useId();
  const previewId = useId();

  const setSort = (next: SortState | null) => {
    if (controlledSort === undefined) setInnerSort(next);
    onSortChange?.(next);
  };
  const cycle = (column: Column<T>) => {
    if (sort?.columnId !== column.id) setSort({ columnId: column.id, direction: "asc" });
    else if (sort.direction === "asc") setSort({ columnId: column.id, direction: "desc" });
    else setSort(null);
  };
  const setDensity = (next: Density) => {
    if (controlledDensity === undefined) setInnerDensity(next);
    onDensityChange?.(next);
  };
  const setSelected = (next: Set<string>) => {
    if (selectedKeys === undefined) setInnerSelected(next);
    onSelectionChange?.(next);
  };

  const keys = sorted.map(rowKey);
  const allOn = keys.length > 0 && keys.every((k) => selected.has(k));
  const someOn = !allOn && keys.some((k) => selected.has(k));
  const headCheck = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (headCheck.current) headCheck.current.indeterminate = someOn;
  }, [someOn]);

  // Row preview: one card for the table, opened after a delay on hover or at once on keyboard focus.
  const [preview, setPreview] = useState<{ key: string; row: T; anchor: DOMRect } | null>(null);
  const previewTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(previewTimer.current), []);
  const openPreview = (row: T, key: string, element: HTMLElement, delay: number) => {
    if (!rowPreview) return;
    window.clearTimeout(previewTimer.current);
    previewTimer.current = window.setTimeout(() => {
      setPreview({ key, row, anchor: element.getBoundingClientRect() });
    }, delay);
  };
  const closePreview = (delay = 100) => {
    window.clearTimeout(previewTimer.current);
    previewTimer.current = window.setTimeout(() => setPreview(null), delay);
  };
  useEffect(() => {
    if (!preview) return;
    const dismiss = () => setPreview(null);
    window.addEventListener("scroll", dismiss, true);
    return () => window.removeEventListener("scroll", dismiss, true);
  }, [preview]);

  const onRowClick = (event: MouseEvent<HTMLTableRowElement>, row: T) => {
    if (!onRowOpen) return;
    const hit = (event.target as HTMLElement).closest(
      "a, button, input, select, textarea, label, [role='button'], [role='menuitem']",
    );
    if (hit && hit !== event.currentTarget) return;
    if (window.getSelection()?.toString()) return;
    window.clearTimeout(previewTimer.current);
    setPreview(null);
    onRowOpen(row);
  };
  const onRowKey = (event: KeyboardEvent<HTMLTableRowElement>, row: T) => {
    if (event.key === "Escape" && preview) {
      setPreview(null);
      return;
    }
    if (event.target !== event.currentTarget) return;
    if (onRowOpen && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      window.clearTimeout(previewTimer.current);
      setPreview(null);
      onRowOpen(row);
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      const sibling =
        event.key === "ArrowDown"
          ? event.currentTarget.nextElementSibling
          : event.currentTarget.previousElementSibling;
      if (sibling instanceof HTMLElement && sibling.tabIndex === 0) {
        event.preventDefault();
        sibling.focus();
      }
    }
  };

  const extra = (selectable ? 1 : 0) + (rowActions ? 1 : 0);
  const span = visible.length + extra;
  const hideable = columns.filter((c) => c.hideable);
  const pages = pagination ? Math.max(1, Math.ceil(pagination.total / pagination.pageSize)) : 0;
  const from = pagination && pagination.total > 0 ? (pagination.page - 1) * pagination.pageSize + 1 : 0;
  const to = pagination ? Math.min(pagination.page * pagination.pageSize, pagination.total) : 0;

  const showEmpty = !loading && !error && sorted.length === 0;
  const hasFooter =
    footer !== undefined || pagination || showDensityToggle || hideable.length > 0 || selected.size > 0;

  const headerFor = (column: Column<T>, first: boolean) => {
    const active = sort?.columnId === column.id ? sort.direction : null;
    const menu: MenuItem[] = [];
    if (column.sortable) {
      menu.push(
        { id: "asc", label: "Sort ascending", onSelect: () => setSort({ columnId: column.id, direction: "asc" }) },
        { id: "desc", label: "Sort descending", onSelect: () => setSort({ columnId: column.id, direction: "desc" }) },
        { id: "clear", label: "Clear sort", disabled: !active, onSelect: () => setSort(null) },
      );
    }
    if (column.hideable) {
      menu.push({
        id: "hide",
        label: "Hide column",
        onSelect: () => setHidden(new Set([...hidden, column.id])),
      });
    }
    return (
      <th
        key={column.id}
        scope="col"
        aria-sort={column.sortable ? (active === "asc" ? "ascending" : active === "desc" ? "descending" : "none") : undefined}
        className={`${styles.th} ${styles[`align_${column.align ?? "left"}`]} ${
          stickyFirstColumn && first ? styles.stickyCol : ""
        }`}
        style={{ width: column.width, ...(stickyFirstColumn && first ? { left: selectable ? "var(--sel-w)" : 0 } : {}) }}
        data-column={column.id}
      >
        <div className={styles.thInner}>
          {column.sortable ? (
            <button type="button" className={styles.sortBtn} onClick={() => cycle(column)} data-testid={`sort-${column.id}`}>
              {column.header}
              {active === "asc" ? (
                <CaretUp size={12} weight="bold" aria-hidden="true" />
              ) : active === "desc" ? (
                <CaretDown size={12} weight="bold" aria-hidden="true" />
              ) : (
                <CaretUpDown size={12} aria-hidden="true" className={styles.sortIdle} />
              )}
            </button>
          ) : (
            <span>{column.header}</span>
          )}
          {column.hint ? <HeaderHint header={column.header} hint={column.hint} /> : null}
          {menu.length ? (
            <MenuButton
              label={`Options for ${column.header}`}
              items={menu}
              className={`${styles.iconBtn} ${styles.menuBtn}`}
              align={column.align === "right" ? "end" : "start"}
            >
              <CaretDown size={12} weight="bold" aria-hidden="true" />
            </MenuButton>
          ) : null}
        </div>
      </th>
    );
  };

  return (
    <div className={styles.root} data-density={density} data-testid={testId}>
      <div
        ref={scroller}
        className={styles.scroll}
        style={maxHeight ? { maxHeight } : undefined}
        {...(overflows ? { role: "region", tabIndex: 0, "aria-label": caption } : {})}
      >
        <table className={styles.table} aria-busy={loading || undefined} aria-labelledby={captionId}>
          <caption id={captionId} className="visuallyHidden">
            {caption}
          </caption>
          <thead>
            <tr>
              {selectable ? (
                <th scope="col" className={`${styles.th} ${styles.selCol} ${stickyFirstColumn ? styles.stickyCol : ""}`} style={stickyFirstColumn ? { left: 0 } : undefined}>
                  <input
                    ref={headCheck}
                    type="checkbox"
                    className={styles.check}
                    aria-label="Select all rows shown"
                    checked={allOn}
                    disabled={loading || keys.length === 0}
                    onChange={() => {
                      const next = new Set(selected);
                      if (allOn) keys.forEach((k) => next.delete(k));
                      else keys.forEach((k) => next.add(k));
                      setSelected(next);
                    }}
                  />
                </th>
              ) : null}
              {visible.map((column, i) => headerFor(column, i === 0))}
              {rowActions ? (
                <th scope="col" className={`${styles.th} ${styles.actionsCol}`}>
                  <span className="visuallyHidden">Actions</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: loadingRows }, (_, r) => (
                  <tr key={`sk-${r}`} className={styles.skRow} data-testid="table-skeleton-row">
                    {selectable ? <td className={styles.selCol} /> : null}
                    {visible.map((column, c) => (
                      <td key={column.id} className={`${styles.td} ${styles[`align_${column.align ?? "left"}`]}`}>
                        <Skeleton width={`${88 - ((r * 7 + c * 13) % 40)}%`} height={12} style={column.align === "right" ? { marginLeft: "auto" } : undefined} />
                      </td>
                    ))}
                    {rowActions ? <td className={styles.actionsCol} /> : null}
                  </tr>
                ))
              : error
                ? (
                  <tr>
                    <td colSpan={span} className={styles.stateCell}>
                      <div role="alert" data-testid="table-error">
                        {error}
                      </div>
                    </td>
                  </tr>
                )
                : showEmpty
                  ? (
                    <tr>
                      <td colSpan={span} className={styles.stateCell} data-testid="table-empty">
                        {empty ?? <EmptyState compact title="Nothing to show" />}
                      </td>
                    </tr>
                  )
                  : sorted.map((row, rowIndex) => {
                    const key = keys[rowIndex] as string;
                    const isSelected = selected.has(key);
                    const label = rowLabel?.(row);
                    return (
                      <tr
                        key={key}
                        role="row"
                        className={`${styles.row} ${onRowOpen ? styles.rowOpen : ""} ${isSelected ? styles.rowSelected : ""}`}
                        tabIndex={onRowOpen ? 0 : undefined}
                        aria-describedby={preview?.key === key ? previewId : undefined}
                        data-testid="table-row"
                        data-row-key={key}
                        onClick={(event) => onRowClick(event, row)}
                        onKeyDown={(event) => onRowKey(event, row)}
                        onPointerEnter={(event) => {
                          if (event.pointerType !== "touch") openPreview(row, key, event.currentTarget, previewDelayMs);
                        }}
                        onPointerLeave={() => closePreview()}
                        onFocus={(event) => {
                          if (event.target === event.currentTarget) openPreview(row, key, event.currentTarget, 0);
                        }}
                        onBlur={(event) => {
                          if (event.target === event.currentTarget) closePreview(0);
                        }}
                      >
                        {selectable ? (
                          <td className={`${styles.td} ${styles.selCol} ${stickyFirstColumn ? styles.stickyCol : ""}`} style={stickyFirstColumn ? { left: 0 } : undefined}>
                            <input
                              type="checkbox"
                              className={styles.check}
                              aria-label={`Select ${label ?? "row"}`}
                              checked={isSelected}
                              onChange={() => {
                                const next = new Set(selected);
                                if (isSelected) next.delete(key);
                                else next.add(key);
                                setSelected(next);
                              }}
                            />
                          </td>
                        ) : null}
                        {visible.map((column, i) => (
                          <td
                            key={column.id}
                            className={`${styles.td} ${styles[`align_${column.align ?? "left"}`]} ${stickyFirstColumn && i === 0 ? styles.stickyCol : ""}`}
                            style={stickyFirstColumn && i === 0 ? { left: selectable ? "var(--sel-w)" : 0 } : undefined}
                          >
                            {column.cell ? column.cell(row) : (column.accessor?.(row) ?? "")}
                          </td>
                        ))}
                        {rowActions ? <td className={`${styles.td} ${styles.actionsCol}`}>{rowActions(row)}</td> : null}
                      </tr>
                    );
                  })}
          </tbody>
        </table>
      </div>
      {preview && rowPreview ? (
        <PreviewCard
          anchor={preview.anchor}
          id={previewId}
          testId="row-preview"
          onPointerEnter={() => window.clearTimeout(previewTimer.current)}
          onPointerLeave={() => closePreview()}
        >
          {rowPreview(preview.row)}
        </PreviewCard>
      ) : null}
      {hasFooter ? (
        <div className={styles.footer} data-testid="table-footer">
          <div className={styles.footLeft}>
            {selected.size > 0 ? (
              <span className={styles.selectedCount} data-testid="table-selected-count">
                {number.format(selected.size)} selected
              </span>
            ) : null}
            {footer ??
              (pagination ? (
                <span data-testid="table-range">
                  Showing {number.format(from)} to {number.format(to)} of {number.format(pagination.total)}
                </span>
              ) : null)}
          </div>
          <div className={styles.footRight}>
            {hideable.length > 0 ? (
              <MenuButton
                label="Columns"
                align="end"
                className={styles.footBtn}
                items={hideable.map((c) => ({
                  id: c.id,
                  label: c.header,
                  checked: !hidden.has(c.id),
                  keepOpen: true,
                  onSelect: () => {
                    const next = new Set(hidden);
                    if (next.has(c.id)) next.delete(c.id);
                    else next.add(c.id);
                    setHidden(next);
                  },
                }))}
              >
                <ColumnsPlusRight size={14} aria-hidden="true" />
                Columns
              </MenuButton>
            ) : null}
            {showDensityToggle ? (
              <div className={styles.seg} role="group" aria-label="Row density">
                <button type="button" className={styles.segBtn} aria-pressed={density === "comfortable"} aria-label="Comfortable rows" title="Comfortable rows" onClick={() => setDensity("comfortable")}>
                  <Rows size={15} aria-hidden="true" />
                </button>
                <button type="button" className={styles.segBtn} aria-pressed={density === "compact"} aria-label="Compact rows" title="Compact rows" onClick={() => setDensity("compact")}>
                  <ListDashes size={15} aria-hidden="true" />
                </button>
              </div>
            ) : null}
            {pagination ? (
              <nav className={styles.pager} aria-label="Pages">
                <button type="button" className={styles.footBtn} disabled={pagination.page <= 1} onClick={() => pagination.onPage(pagination.page - 1)}>
                  Previous
                </button>
                <span className={styles.pageOf} data-testid="table-page">
                  Page {number.format(pagination.page)} of {number.format(pages)}
                </span>
                <button type="button" className={styles.footBtn} disabled={pagination.page >= pages} onClick={() => pagination.onPage(pagination.page + 1)}>
                  Next
                </button>
              </nav>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
